import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import UsageMeter from './UsageMeter';
import { getEnabledModules, getLockedModules } from '../../utils/moduleAccess';

const USAGE_KEYS = ['students', 'teachers', 'classes', 'sections'];

const USAGE_LABELS = {
  students: 'Students',
  teachers: 'Teachers',
  classes: 'Classes',
  sections: 'Sections',
};

export default function SubscriptionSummaryCard({ user, compact = false }) {
  const sub = user?.subscription;
  if (!sub || user?.role_slug === 'super_admin') return null;

  const enabled = getEnabledModules(user);
  const locked = getLockedModules(user);
  const usage = sub.usage || {};

  if (compact) {
    return (
      <Card className="border-border">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Current plan</p>
            <p className="font-semibold">{sub.plan_name || '—'}</p>
          </div>
          <SubscriptionStatusBadge status={sub.status} />
          <Button size="sm" variant="outline" asChild>
            <Link to="/settings/subscription">Manage</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg">Subscription</CardTitle>
        <SubscriptionStatusBadge status={sub.status} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Current Plan</p>
            <p className="font-medium">{sub.plan_name || '—'}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Status</p>
            <p className="font-medium capitalize">{sub.status || '—'}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Expiry Date</p>
            <p className="font-medium">
              {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Days Remaining</p>
            <p className="font-medium">{sub.days_remaining ?? 0}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">{enabled.length} modules enabled</Badge>
          {locked.length > 0 && (
            <Badge variant="outline" className="text-amber-700 border-amber-500/40">
              {locked.length} locked
            </Badge>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {USAGE_KEYS.map((key) => {
            const row = usage[key];
            if (!row) return null;
            return (
              <UsageMeter
                key={key}
                label={USAGE_LABELS[key]}
                used={row.used}
                limit={row.limit}
              />
            );
          })}
        </div>

        <Button variant="outline" size="sm" asChild>
          <Link to="/settings/subscription">
            <ArrowUpRight className="w-4 h-4 mr-1" />
            Subscription settings
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

export function PlanFeaturesSection({ user, onUpgradeModule }) {
  const enabled = getEnabledModules(user);
  const locked = getLockedModules(user);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan Features</CardTitle>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Included Modules
          </h4>
          <ul className="space-y-2 text-sm">
            {enabled.map((m) => (
              <li key={m.key} className="flex items-center gap-2 text-foreground">
                <CheckCircle className="w-3.5 h-3.5 text-green-600 shrink-0" />
                {m.label}
              </li>
            ))}
            {enabled.length === 0 && (
              <li className="text-muted-foreground">No modules included</li>
            )}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            Locked Modules
          </h4>
          <ul className="space-y-3 text-sm">
            {locked.map((m) => (
              <li key={m.key} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-3.5 h-3.5 shrink-0" />
                  {m.label}
                  <Badge variant="secondary" className="text-[10px]">Upgrade Required</Badge>
                </span>
                <Button size="sm" variant="outline" onClick={() => onUpgradeModule?.(m)}>
                  Upgrade Plan
                </Button>
              </li>
            ))}
            {locked.length === 0 && (
              <li className="text-muted-foreground">All modules are unlocked</li>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
